import { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useSelector } from "react-redux";
import UserManagement from "../components/UserManagement";
import ConfirmationDialog from "../components/ConfirmationDialog";
import HistoryModal from "../components/HistoryModal";
import AlertMessage from "../components/AlertMessage";
import { usePageLogger } from "../hooks/usePageLogger";
import SnackbarAlert from "../components/SnackBarAlert";
import UserEditModal from "../components/User/UserEditModal";
import UserTable from "../components/User/UserTable";
import * as userHandlers from "../components/User/utils/userHandlers";

const UserList = () => {
  const {
    token,
    role: currentUserRole,
    userId,
  } = useSelector((state) => state.auth);
  const { user } = useSelector((state) => state.auth);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedUserHistory, setSelectedUserHistory] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openUserPopup, setOpenUserPopup] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  usePageLogger("UserList Page", {
    username: user?.username,
    role: user?.role,
  });

  // Fetch users from API
  const fetchUsersCallback = useCallback(async () => {
    await userHandlers.fetchUsers(token, user, setUsers, setFeedback);
  }, [token, user]);

  useEffect(() => {
    fetchUsersCallback();
  }, [fetchUsersCallback]);

  // Edit handlers
  const handleEditClick = useCallback(
    (data) => {
      userHandlers.handleEditClick(data, user, setEditUser, setEditModalOpen);
    },
    [user]
  );

  const handleEditModalClose = useCallback(() => {
    userHandlers.handleEditModalClose(
      editModalOpen,
      editUser,
      user,
      setEditModalOpen
    );
  }, [editModalOpen, editUser, user]);

  const handleEditSave = useCallback(
    async (userData) => {
      await userHandlers.handleEditSave(
        userData,
        token,
        user,
        setUsers,
        setEditModalOpen,
        setFeedback
      );
    },
    [token, user]
  );

  // Delete handlers
  const handleDeleteClick = useCallback(
    (data) => {
      userHandlers.handleDeleteClick(
        data,
        userId,
        user,
        setOpenAlert,
        setUserToDelete,
        setDeleteDialogOpen
      );
    },
    [user, userId]
  );

  const handleDeleteConfirm = useCallback(async () => {
    await userHandlers.handleDeleteConfirm(
      userToDelete,
      token,
      user,
      setFeedback,
      setDeleteDialogOpen,
      setUserToDelete,
      fetchUsersCallback
    );
  }, [userToDelete, token, user, fetchUsersCallback]);

  const handleDeleteCancel = useCallback(() => {
    userHandlers.handleDeleteCancel(user, setDeleteDialogOpen, setUserToDelete);
  }, [user]);

  const handleViewHistory = useCallback(
    (data) => {
      userHandlers.handleViewHistory(
        data,
        user,
        setHistoryModalOpen,
        setSelectedUserHistory,
        setFeedback
      );
    },
    [user]
  );

  const handleHistoryClose = () => {
    userHandlers.handleHistoryClose(
      user,
      setHistoryModalOpen,
      setSelectedUserHistory
    );
  };

  // Add User button handler
  const handleAddUser = useCallback(() => {
    userHandlers.handleAddUser(user, setOpenUserPopup);
  }, [user]);

  const handleCloseFeedback = useCallback(() => {
    userHandlers.handleCloseFeedback(feedback, setFeedback);
  }, [feedback]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Users List
        </Typography>
        {(currentUserRole === "superadmin" ||
          currentUserRole === "admin1" ||
          currentUserRole === "admin2") && (
          <Button variant="contained" color="primary" onClick={handleAddUser}>
            Add User
          </Button>
        )}
      </Box>

      <UserTable
        users={users}
        currentUserId={userId}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onViewHistory={handleViewHistory}
      />

      <UserEditModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        user={editUser}
        onSave={handleEditSave}
        currentUserId={userId}
      />

      <HistoryModal
        title=" Users Updated History"
        open={historyModalOpen}
        onClose={handleHistoryClose}
        history={selectedUserHistory}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete user ${userToDelete?.username}?`}
      />
      <AlertMessage
        title="User Delete Alert"
        open={openAlert}
        setOpen={setOpenAlert}
        message="You cannot delete the current user. If you want to delete this user, please log in with another account."
      />

      {openUserPopup && (
        <UserManagement
          open={openUserPopup}
          onClose={() => {
            setOpenUserPopup(false);
            fetchUsersCallback();
          }}
        />
      )}

      <SnackbarAlert feedback={feedback} onClose={handleCloseFeedback} />
    </Box>
  );
};

export default UserList;
